import { collection, query, where, getDocs, updateDoc, doc, getDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../lib/firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import type { Order } from '../types';

export interface Invoice {
  id: string;
  orderId: string;
  orderNumber: string;
  buyerName: string;
  sellers: Array<{
    name: string;
    companyId: string;
    vat: string;
    hasCommissionInvoice?: boolean;
    commissionPdfUrl?: string;
    commission: number;
  }>;
  total: number;
  createdAt: string;
  pdfUrl: string;
  status: 'faturado' | 'incompleto' | 'por_faturar';
  isOnlineSale: boolean;
}

async function getCompanyDetails(companyId: string): Promise<{ name: string; vat: string; email: string }> {
  try {
    console.log('Fetching company details for ID:', companyId);
    const companyRef = doc(db, 'companies', companyId);
    const companyDoc = await getDoc(companyRef);
    const data = companyDoc.data();
    
    if (!data) {
      throw new Error(`Company not found: ${companyId}`);
    }

    return {
      name: data.commercialName || data.legalName || 'N/A',
      vat: data.VAT || data.vat || 'N/A',  
      email: data.commercialEmail || data.email || 'N/A'
    };
  } catch (error) {
    console.error('Error getting company details:', error);
    throw error;
  }
}

export async function deleteInvoicePdf(orderId: string, type: 'client' | 'commission', sellerId?: string) {
  try {
    // 1. Primeiro atualizar o banco de dados
    if (type === 'client') {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        invoicePdf: null,
        invoiceStatus: 'pending',
        invoiced: false,
        updatedAt: serverTimestamp()
      });
    } else if (type === 'commission' && sellerId) {
      const walletTransactionsRef = collection(db, 'walletTransactions');
      const q = query(
        walletTransactionsRef,
        where('orderId', '==', orderId),
        where('type', '==', 'commission'),
        where('vendorId', '==', sellerId)
      );
      
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const transactionDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, 'walletTransactions', transactionDoc.id), {
          invoicePdf: null,
          updatedAt: serverTimestamp()
        });
      }
    }

    // 2. Tentar deletar do Storage se existir
    try {
      const storagePath = type === 'client' 
        ? `invoices/${orderId}/invoice.pdf`
        : `commission-invoices/${orderId}/${sellerId}.pdf`;
      
      const fileRef = ref(storage, storagePath);
      await deleteObject(fileRef);
    } catch (storageError: any) {
      // Se o arquivo não existe no storage, apenas log o erro mas não falha a operação
      if (storageError.code === 'storage/object-not-found') {
        console.log('File not found in storage, continuing with database update');
      } else {
        throw storageError;
      }
    }

    return true;
  } catch (error) {
    console.error('Error deleting invoice:', error);
    throw new Error('Failed to delete invoice');
  }
}

export async function uploadInvoicePdf(
  orderId: string,
  file: File,
  type: 'client' | 'commission',
  sellerId?: string
) {
  try {
    // 1. Upload file to storage first
    const fileRef = ref(storage, `invoices/${orderId}/${type}${sellerId ? `_${sellerId}` : ''}.pdf`);
    await uploadBytes(fileRef, file);
    const downloadUrl = await getDownloadURL(fileRef);
    
    // 2. Then update the database with the URL
    if (type === 'client') {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, {
        invoicePdf: downloadUrl,
        invoiceStatus: 'invoiced',
        updatedAt: serverTimestamp()
      });
    } else if (type === 'commission' && sellerId) {
      const walletTransactionsRef = collection(db, 'walletTransactions');
      const q = query(
        walletTransactionsRef,
        where('orderId', '==', orderId),
        where('type', '==', 'commission'),
        where('vendorId', '==', sellerId)
      );
      
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const transactionDoc = querySnapshot.docs[0];
        await updateDoc(doc(db, 'walletTransactions', transactionDoc.id), {
          invoicePdf: downloadUrl,
          updatedAt: serverTimestamp()
        });
      }
    }
    
    return downloadUrl;
  } catch (error) {
    console.error('Error uploading invoice:', error);
    throw new Error('Failed to upload invoice');
  }
}

export async function uploadCommissionInvoice(
  orderNumber: string,
  vendorId: string,
  file: File
): Promise<string> {
  try {
    // 1. Upload the PDF to Firebase Storage
    const storageRef = ref(storage, `commission-invoices/${orderNumber}/${vendorId}.pdf`);
    await uploadBytes(storageRef, file);
    const pdfUrl = await getDownloadURL(storageRef);

    // 2. Update the wallet transaction with the PDF URL
    const walletTransactionsRef = collection(db, 'walletTransactions');
    const q = query(
      walletTransactionsRef,
      where('orderId', '==', orderNumber),
      where('type', '==', 'commission'),
      where('vendorId', '==', vendorId)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      throw new Error('Commission transaction not found');
    }

    // Update the transaction with the invoice PDF URL
    const transactionDoc = querySnapshot.docs[0];
    await updateDoc(transactionDoc.ref, {
      invoicePdf: pdfUrl,
      invoiceUploadedAt: serverTimestamp()
    });

    return pdfUrl;
  } catch (error) {
    console.error('Error uploading commission invoice:', error);
    throw error;
  }
}

export async function uploadInvoice(file: File, path: string): Promise<string> {
  try {
    const storageRef = ref(storage, path);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    return downloadURL;
  } catch (error) {
    console.error('Error uploading invoice:', error);
    throw error;
  }
}

export async function getInvoicesForCompany(companyId: string | null): Promise<Invoice[]> {
  try {
    const ordersRef = collection(db, 'orders');
    let q;

    if (companyId) {
      q = query(
        ordersRef,
        where('sellerId', '==', companyId),
        where('status', 'not-in', ['cancelled', 'returned'])
      );
    } else {
      q = query(
        ordersRef,
        where('isOnlineSale', '==', true),
        where('status', 'not-in', ['cancelled', 'returned'])
      );
    }

    const querySnapshot = await getDocs(q);
    const invoices: Invoice[] = [];

    for (const docSnapshot of querySnapshot.docs) {
      const orderData = docSnapshot.data();

      // Primeiro buscar todas as transações de comissão desta ordem
      const walletTransactionsRef = collection(db, 'walletTransactions');
      const commissionsQuery = query(
        walletTransactionsRef,
        where('orderId', '==', orderData.orderNumber),
        where('type', '==', 'commission')
      );
      
      const commissionsSnapshot = await getDocs(commissionsQuery);
      const commissionsByVendor = new Map();
      commissionsSnapshot.docs.forEach(doc => {
        const transaction = doc.data();
        commissionsByVendor.set(transaction.vendorId, transaction);
      });

      // Buscar informações dos vendedores
      const sellers = [];
      if (orderData.items && Array.isArray(orderData.items)) {
        const uniqueSellerIds = new Set(orderData.items.map(item => item.companyId).filter(Boolean));
        
        for (const sellerId of uniqueSellerIds) {
          try {
            const sellerDocRef = doc(db, 'companies', sellerId);
            const sellerDoc = await getDoc(sellerDocRef);
            if (sellerDoc.exists()) {
              const sellerData = sellerDoc.data();
              
              // Pegar a transação de comissão deste vendedor
              const commissionTx = commissionsByVendor.get(sellerId);
              
              // Encontrar a fatura de comissão nos sellers se existir
              const existingSeller = orderData.sellers?.find(s => s.id === sellerId);
              const hasInvoice = existingSeller?.invoicePdf || commissionTx?.invoicePdf;
              
              sellers.push({
                id: sellerId,
                name: sellerData.commercialName || sellerData.legalName,
                vat: sellerData.VAT || 'undefined',
                commission: commissionTx?.amount || 0,
                commissionPdfUrl: hasInvoice || null
              });
            }
          } catch (err) {
            console.error(`Error fetching seller ${sellerId}:`, err);
          }
        }
      }

      // Determinar o status da fatura
      let status = 'por_faturar';
      const hasClientInvoice = !!orderData.invoicePdf;
      const allSellersHaveInvoice = sellers.length > 0 && sellers.every(s => s.commissionPdfUrl);
      
      if (hasClientInvoice && allSellersHaveInvoice) {
        status = 'faturado';
      } else if (hasClientInvoice || sellers.some(s => s.commissionPdfUrl)) {
        status = 'incompleto';
      }

      invoices.push({
        id: docSnapshot.id,
        orderId: docSnapshot.id,
        orderNumber: orderData.orderNumber || docSnapshot.id,
        buyerName: orderData.shippingAddress?.name || orderData.customer?.name || 'N/A',
        sellers,
        isOnlineSale: orderData.isOnlineSale || false,
        createdAt: orderData.createdAt,
        total: orderData.total || 0,
        status,
        pdfUrl: orderData.invoicePdf || null
      });
    }

    return invoices.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  } catch (error) {
    console.error('Error getting invoices:', error);
    throw new Error('Failed to get invoices');
  }
}

export function downloadInvoicePdf(pdfUrl: string, orderNumber: string) {
  const link = document.createElement('a');
  link.href = pdfUrl;
  link.download = `invoice-${orderNumber}.pdf`;
  link.target = '_blank';
  link.click();
}
